import React, { useEffect, useRef, useState } from "react";
import {
  postDetails,
  postMultipartDetails,
} from "../../services/http/http.service";
import APIList from "../../utils/constants/APIList";
import { useNavigate } from "react-router-dom";
import "./ConnectTable.css";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import encrypt from "../../utils/helpers/encrypt";
import { validateImageFile } from "../../utils/helpers/validateFileUpload";
import CommonTable, { TableColumn } from "../../components/Table/CommonTable";
import { useAppDispatch } from "../../store/storeRequests";
import { hideToast, showToast } from "../../store/Toast/toast.slice";
import { hideLoader, showLoader } from "../../store/Loader/loader.slice";

export interface ConnectTableData {
  id: number;
  title: string;
  is_folder: boolean;
  modified: Date;
  image_name?: string;
}

const ITEMS_PER_PAGE = 8;

const ConnectTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ConnectTableData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<ConnectTableData | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newIsFolder, setNewIsFolder] = useState(false);
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [validationError, setValidationError] = useState<string>("");
  const dispatch = useAppDispatch();
  const [editImageName, setEditImageName] = useState<string | null>(null);
  const [newImagePreview, setNewImagePreview] = useState<string | null>(null);
  const isUIMounted = useRef(false);
  const handleShowAddModal = () => {
    setNewTitle("");
    setNewIsFolder(false);
    setNewImageFile(null);
    setNewImagePreview(null); // Reset the image preview
    setEditMode(false);
    setEditItem(null);
    setValidationError("");
    setShowAddModal(true);
  };
  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewImageFile(null);
    setNewImagePreview(null);
  };
  useEffect(() => {
    if (isUIMounted.current) return;
    isUIMounted.current = true;
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      dispatch(showLoader("Loading data..."));

      const response = await postDetails(APIList.GET_MAIN_FOLDERS, {});
      console.log(response);
      const sortedItems = response.data.reduce(
        (acc: ConnectTableData[], item: ConnectTableData) => {
          const index = acc.findIndex(
            (i) => new Date(i.modified) < new Date(item.modified)
          );
          if (index === -1) {
            acc.push(item);
          } else {
            acc.splice(index, 0, item);
          }
          return acc;
        },
        []
      );
      setData(sortedItems);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleEdit = (item: ConnectTableData) => {
    setEditItem(item);
    setNewTitle(item.title);
    setNewIsFolder(item.is_folder);
    setEditImageName(item.image_name || null);
    setShowAddModal(true);
    setEditMode(true);
  };

  const handleDelete = async (item: ConnectTableData) => {
    try {
      const response = await postDetails(APIList.DELETE_MAIN_FOLDER, {
        id: item.id,
      });
      if (response.statusCode === 200) {
        console.log("Item deleted successfully.");
        dispatch(showToast("Deleted Successfully."));
        setTimeout(() => dispatch(hideToast()), 3000);
        fetchData();
        setData((prevData) =>
          prevData.filter((dataItem) => dataItem.id !== item.id)
        );
      } else {
        console.error("Failed to delete item:", response.message);
      }
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  // const handleView = (item: ConnectTableData) => {
  //   if (item.is_folder) {
  //     navigate(`/my-connect/${item.id}/${item.title}`);
  //   } else {
  //     navigate(`/item-details/${item.id}/${item.title}`);
  //   }
  // };

  const handleView = (item: ConnectTableData) => {
    const sanitizedTitle = item.title.replace(/\//g, "");

    if (item.is_folder) {
      navigate(`/my-connect/${item.id}/${sanitizedTitle}`);
    } else {
      navigate(`/item-details/${item.id}/${sanitizedTitle}`);
    }
  };


  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;

    if (file) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        dispatch(showToast(validation.message));
        setTimeout(() => dispatch(hideToast()), 3000);
        event.target.value = "";
        setNewImageFile(null);
        setNewImagePreview(null); // Clear preview
      } else {
        setNewImageFile(file);
        setNewImagePreview(URL.createObjectURL(file)); // Set preview
      }
    }
  };

  const handleAddSubmit = async () => {
    try {
      const trimmedTitle = newTitle.trim();
      console.log(trimmedTitle)
      if (!trimmedTitle) {
        // alert("Please fill all the required fields.");
        // setValidationError("Title is required.");
        dispatch(showToast("Title is required."));
        setTimeout(() => dispatch(hideToast()), 3000);
        return;
      }

      const isTitleValid = /^[a-zA-Z0-9\s!.'/\-@]+$/.test(trimmedTitle.trim());



      if (!isTitleValid) {
        // alert("Please fill a valid title. Title cannot be just numbers.");
        return;
      }

      if (trimmedTitle.length < 3) {
        // setValidationError("Title must be at least 3 characters long.");
        dispatch(showToast("Title must be at least 3 characters long."));
        setTimeout(() => dispatch(hideToast()), 3000);
        return;
      }

      if (!editMode && !newImageFile) {
        dispatch(showToast("Image file is required for new items."));
        setTimeout(() => dispatch(hideToast()), 3000);
        return;
      }

      const formData = new FormData();
      const payload = {
        id: editMode && editItem ? editItem.id : undefined,
        title: trimmedTitle,
        is_folder: newIsFolder ? 1 : 0,
        myconnect_master_id: editMode && editItem ? editItem.id : undefined,
      };
      console.log(payload)

      const hasChanges =
        trimmedTitle !== (editMode && editItem ? editItem.title.trim() : "") ||
        newIsFolder !== (editMode && editItem ? editItem.is_folder : false) ||
        newImageFile !== null;

      if (!editMode || hasChanges) {
        formData.append("data", encrypt(payload));

        if (newImageFile) {
          formData.append("mediafiles", newImageFile);
        }

        const apiEndpoint = editMode ? APIList.EDIT_DATA : APIList.ADD_CONNECT;

        try {
          const response = await postMultipartDetails(apiEndpoint, formData);
          if (response.statusCode === 200) {
            console.log(
              `${editMode ? "Data updated" : "Data added"} successfully.`
            );
            fetchData();

            // Show appropriate toast message based on editMode
            dispatch(
              showToast(
                editMode ? "Updated Successfully." : "Added Successfully."
              )
            );

            setTimeout(() => dispatch(hideToast()), 3000);
          } else {
            dispatch(showToast("Please fill form properly or User can not add with same folder name"));
            setTimeout(() => dispatch(hideToast()), 3000);
            console.error(
              `Failed to ${editMode ? "update" : "add"} data:`,
              response.message
            );
          }
        } catch (error) {

          console.error(`Error ${editMode ? "updating" : "adding"} data:`, error);
        } finally {
          setShowAddModal(false);
          setEditMode(false);
          setEditItem(null);
          setNewTitle("");
          setNewIsFolder(false);
          setNewImageFile(null);
          setValidationError("");
        }
      } else {
        // No changes detected during edit
        dispatch(showToast("No Changes Done."));
        setTimeout(() => dispatch(hideToast()), 3000);
        // setShowAddModal(false);
      }
    } catch (e) {
      console.log(e)
    }
  };


  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleSortToggle = () => {
    setSortAscending(!sortAscending);
  };

  const filteredData = data.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedData = filteredData.sort((a, b) => {
    if (a.title < b.title) return sortAscending ? -1 : 1;
    if (a.title > b.title) return sortAscending ? 1 : -1;
    return 0;
  });

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPageData = sortedData.slice(startIndex, endIndex);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const columns: TableColumn[] = [
    { key: "image_name", label: "Image", dataType: "image" },
    { key: "title", label: "Title", sortable: true, dataType: "string" },
    {
      key: "",
      label: "Folder / Items",
      dataType: "button",
      button: {
        label: (item: ConnectTableData) => (
          <span className="view-button">
            {item.is_folder ? "View Folders" : "View Item"}
          </span>
        ),
        onClick: handleView,
      },
    },
  ];

  return (
    <div className="table-container">
      <div className="add-btn-container">
        <button
          className="my-connect-add-btn text-white align-items-center justify-content-center "
          onClick={handleShowAddModal}
        >
          Add Data
        </button>
      </div>

      <CommonTable
        columns={columns}
        data={data}
        pagination={true}
        onDelete={handleDelete}
        onEdit={handleEdit}
        rowsPerPage={10}
        actions={{
          delete: true,
          edit: true,
        }}
      />
      <Modal show={showAddModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? "Edit Data" : "Add New Data"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Title Field */}
            <Form.Group controlId="formTitle">
              <Form.Label>
                Title {!editMode && <span style={{ color: "red" }}>*</span>}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title"
                value={newTitle}
                onChange={(e) => {
                  if (e.target.value.length <= 50) {
                    setNewTitle(e.target.value);
                  }
                }}
                isInvalid={
                  !!validationError &&
                  (newTitle.length < 3 || newTitle.length > 50)
                }
              />
              <Form.Control.Feedback type="invalid">
                Title is required, must be at least 3 characters
              </Form.Control.Feedback>
              <div className="text-muted">{newTitle.length}/50</div>
            </Form.Group>

            {/* Is Folder Checkbox */}
            <Form.Group controlId="formIsFolder">
              <Form.Check
                type="checkbox"
                label="Is Folder"
                checked={newIsFolder}
                onChange={(e) => setNewIsFolder(e.target.checked)}
                disabled={editMode}
              />
            </Form.Group>
            <Form.Group controlId="formImage">
              <div>
                {editMode && (


                  <div className="image-preview">
                    {newImagePreview ? (
                      <img
                        src={newImagePreview}
                        alt="New Preview"
                        style={{ width: "100px", height: "100px" }}
                      />
                    ) : (
                      editImageName && (
                        <img
                          src={editImageName}
                          alt="Existing"
                          style={{ width: "100px", height: "100px" }}
                        />
                      )
                    )}
                  </div>


                )}
                <Form.Label>
                  Select Image {!editMode && <span style={{ color: "red" }}>*</span>}
                </Form.Label>

                <div className="custom-file">
                  <Form.Control
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="custom-file-input"
                  />
                </div>
                <Form.Text className="text-muted">
                  Uploads must be JPG/JPEG/PNG
                </Form.Text>
              </div>
            </Form.Group>
            {/* <Form.Group controlId="formImage">
  <div>
    {editMode && (
      <div className="image-preview">
        {newImagePreview ? (
          <img
            src={newImagePreview}
            alt="New Preview"
            style={{ width: "100px", height: "100px" }}
          />
        ) : (
          editImageName && (
            <img
              src={editImageName}
              alt="Existing"
              style={{ width: "100px", height: "100px" }}
            />
          )
        )}
      </div>
    )}
    <Form.Label>
      Select Image {!editMode && !newIsFolder && <span style={{ color: "red" }}>*</span>}
    </Form.Label>
    <div className="custom-file">
      <Form.Control
        type="file"
        accept=".jpg,.jpeg,.png"
        onChange={handleFileChange}
        className="custom-file-input"
        disabled={newIsFolder}  // Disable the file input if it's a folder
      />
    </div>
    <Form.Text className="text-muted">
      Uploads must be JPG/JPEG/PNG
    </Form.Text>
  </div>
</Form.Group> */}
            {validationError && <Alert variant="danger">{validationError}</Alert>}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Close
          </Button>
          <button className="save-changes" onClick={handleAddSubmit}>
            {editMode ? "Save Changes" : "Add"}
          </button>
        </Modal.Footer>
      </Modal>



    </div>
  );
};

export default ConnectTable;
